import React from 'react';
import {Box} from 'src/design/atoms/Box';
import {Stack} from 'src/design/atoms/Stack';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import type {
  WorkflowDefinitionSimpleResponse} from 'src/api/ProactApiClient';
import {
  ModuleEnum,
  PermissionOperationEnum
} from 'src/api/ProactApiClient';
import {ProjectSelectBox} from 'src/app/databound-elements/ProjectSelectBox';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {PermissionGuard} from 'src/app/security/authorization/PermissionGuard';
import {DataList} from 'src/design/organisms/DataList';
import {useListState} from 'src/design/organisms/useListState';
import {useGetWorkflowDefinitionListQuery} from 'src/redux/queries/workflowDefinition';
import useDebounce from 'src/shared/hooks/useDebounce';
import {useProjectScope} from 'src/shared/hooks/useProjectScope';
import {WorkflowPillConfig} from 'src/app/workflows/workflowHelpers';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const maxPageRows = 10;

interface ManageWorkflowsFilters {
  selectedProjectId: string | null;
}

export const ManageWorkflows: React.FC = () => {
  const navigate = useAppNavigate();
  const {
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    currentPage,
    handlePageChange,
    start,
    length,
  } = useListState<ManageWorkflowsFilters>({
    identifier: 'workflow-definitions-list',
    pageSize: maxPageRows,
    defaultFilters: {selectedProjectId: null},
  });
  const {selectedProjectId} = filters;
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // The sidebar project scope already narrows the list server-side; the in-page
  // filter is hidden and dropped from the request while it is set.
  const projectScope = useProjectScope();
  const projectId =
    projectScope === null && selectedProjectId
      ? parseInt(selectedProjectId, 10)
      : undefined;

  const {data, isLoading} = useGetWorkflowDefinitionListQuery({
    start,
    length,
    search: debouncedSearchTerm,
    projectId,
  });

  const indexListData = data?.data;
  const totalRecords = data?.totalRecords ?? 0;

  const handleAddNew = () => navigate(PathUtility.WorkflowCreate());

  const handleNavigateTo = (id: string | number) =>
    navigate(PathUtility.WorkflowViewDefinition(id as string));

  const handleProjectChange = (option: {id: string | number; label: string} | null) => {
    setFilters({selectedProjectId: option?.id ? String(option.id) : null});
  };

  return (
    <Stack gap="md">
      <PageHeader>
        <PageHeader.Search
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search workflows..."
        />
        <PageHeader.Actions>
          {projectScope === null && (
            <Box style={{width: '250px'}}>
              <ProjectSelectBox
                placeholder="Filter by project"
                value={selectedProjectId}
                onSelectChangeCallback={handleProjectChange}
              />
            </Box>
          )}
          <PermissionGuard
            module={ModuleEnum.Workflows}
            operation={PermissionOperationEnum.Create}>
            <PageHeader.Action variant="create" onClick={handleAddNew}>
              New Workflow
            </PageHeader.Action>
          </PermissionGuard>
        </PageHeader.Actions>
      </PageHeader>
      <DataList
        isLoading={isLoading}
        data={indexListData}
        currentPage={currentPage}
        totalRecords={totalRecords}
        recordsPerPage={maxPageRows}
        onRowClick={handleNavigateTo}
        onPageChange={handlePageChange}
        onGetPillMapping={(item: WorkflowDefinitionSimpleResponse) =>
          WorkflowPillConfig(item.active)
        }
        onRowTextRender={(item: WorkflowDefinitionSimpleResponse) => item.name}
        onRowSubTextRender={(item: WorkflowDefinitionSimpleResponse) =>
          item.project?.name ?? 'All Projects'
        }
        hasSearchTerm={searchTerm !== ''}
        hasCreationLogic={true}
        meta={undefined}
      />
    </Stack>
  );
};
